import { type ApolloClient, gql } from '@apollo/client/core';
import { Query } from '../../generated/graphql';
import { Query as AdminQuery } from '../../generated/api.graphql';
import * as Artist from './Artist';
import * as SearchResultArtist from './SearchResultArtist';
import * as EventArtist from './EventArtist';
import { VPResult } from '../../api/VPResult';

export const ARTIST_FIELDS = gql`
  fragment ArtistFields on Artist {
    id
    name
    bio
    lastSynchronizedAt
    synchronizationAvailable
    links {
      id
      url
    }
    attachments {
      attachment {
        id
        name
        status
        url
      }
    }
  }
`;

export const ARTIST_SEARCH_QUERY = gql`
  query ArtistSearch($search: String) {
    artists(search: $search) {
      id
      name
      imageUrl
      soundchartsUuid
      countryCode
      genres {
        root
        sub
      }
    }
  }
`;

export const ARTIST_QUERY = gql`
  query Artist($id: Int!) {
    artist(id: $id) {
      ...ArtistFields
    }
  }
  ${ARTIST_FIELDS}
`;

export const SYNC_ARTIST_QUERY = gql`
  mutation SynchronizeArtist($artistId: Int!) {
    syncArtist(input: { artistId: $artistId }) {
      artist {
        ...ArtistFields
      }
    }
  }
  ${ARTIST_FIELDS}
`;

export const searchArtists = async (client: ApolloClient<Query>, query: string) => {
  const { data } = await client.query({
    query: ARTIST_SEARCH_QUERY,
    variables: {
      search: query,
    },
  });

  return SearchResultArtist.fromGqlCollection(data.artists);
};

export const fetchArtist = async (client: ApolloClient<Query>, id: number) => {
  const { data } = await client.query({
    query: ARTIST_QUERY,
    variables: {
      id,
    },
  });

  return Artist.fromGql(data.artist);
};

export const synchronizeArtist = async (client: ApolloClient<Query>, artistId: number) => {
  const { data } = await client.mutate({
    mutation: SYNC_ARTIST_QUERY,
    variables: {
      artistId,
    },
  });

  return Artist.fromGql(data.syncArtist.artist);
};

export const saveArtist = async (client: ApolloClient<Query>, artist: Artist.ArtistInput) => {
  const mutation = gql`
    mutation SaveArtist($artist: ArtistInput!) {
      saveArtist(input: { artist: $artist }) {
        artist {
          ...ArtistFields
        }
        errors {
          path
          message
        }
      }
    }
    ${ARTIST_FIELDS}
  `;

  await client.mutate({
    mutation,
    variables: {
      artist,
    },
  });
};

// `id` is selected for two reasons. It shares the `eventArtists(eventId:)` cache field key with
// `App/Artist`'s rider read, and rows without an id cannot normalize — so one selection would
// overwrite the other's. It also fixes the rows themselves: `EventArtist.id` was declared required
// but never selected, so every row this query produced carried `undefined` there, which is the id
// the announce modal's artist list passes to `removeEventArtist` and `reorderEventArtists`.
const EVENT_ARTISTS_QUERY = gql`
  query GetEventArtists($eventId: Int!) {
    eventArtists(eventId: $eventId) {
      id
      offerSheetId
      headliner
      legacyEventArtistId
      legacySettlementSheetId
      artist {
        ...ArtistFields
      }
    }
  }
  ${ARTIST_FIELDS}
`;

type EventArtistsParams = {
  eventId: number;
};

export const fetchEventArtists = async (client: ApolloClient<AdminQuery>, { eventId }: EventArtistsParams): Promise<EventArtist.EventArtist[]> => {
  const { data } = await client.query({
    query: EVENT_ARTISTS_QUERY,
    fetchPolicy: 'network-only',
    variables: {
      eventId,
    },
  });

  return EventArtist.fromGqlCollection(data.eventArtists);
};

const ADD_EVENT_ARTISTS_MUTATION = gql`
  mutation AddEventArtist($eventId: Int!, $artist: ArtistInput!) {
    addEventArtist(input: { eventId: $eventId, artist: $artist}) {
      eventArtist {
        id
        artist {
          ...ArtistFields
        }
      }
      clientMutationId
      errors
      success
    }
  }
  ${ARTIST_FIELDS}
`;

type AddEventArtistParams = {
  eventId: number;
  artist: {
    id?: number;
    name?: string;
    platformUrl?: string;
  };
};

export const addEventArtist = async (client: ApolloClient<Query>, addParams: AddEventArtistParams): Promise<VPResult<EventArtist.EventArtist>> => {
  const { data } = await client.mutate({
    mutation: ADD_EVENT_ARTISTS_MUTATION,
    variables: {
      ...addParams,
    },
    update: cache => {
      cache.evict({
        id: 'ROOT_QUERY',
        // Probably needs better cache clearing
        fieldName: 'app',
      });
      cache.gc();
    },
  });

  const response = data.addEventArtist;
  if (response.success) {
    return {
      success: true,
      data: EventArtist.fromGql(response.eventArtist),
    };
  }

  return {
    success: false,
    errors: response.errors,
  };
};

const REMOVE_EVENT_ARTIST_MUTATION = gql`
  mutation RemoveEventArtist($eventArtistId: Int!) {
    removeEventArtist(input: { eventArtistId: $eventArtistId}) {
      clientMutationId
      errors
      success
    }
  }
`;

type RemoveEventArtistParams = {
  eventArtistId: number;
};

export const removeEventArtist = async (client: ApolloClient<Query>, removeParams: RemoveEventArtistParams) => {
  const { data } = await client.mutate({
    mutation: REMOVE_EVENT_ARTIST_MUTATION,
    variables: {
      ...removeParams,
    },
    update: cache => {
      cache.evict({
        id: 'ROOT_QUERY',
        // Probably needs better cache clearing
        fieldName: 'app',
      });
      cache.gc();
    },
  });

  const response = data.removeEventArtist;
  if (response.success) {
    return {
      success: true,
    };
  }

  return {
    success: false,
    errors: response.errors,
  };
};

const REORDER_EVENT_ARTISTS_MUTATION = gql`
  mutation ReorderEventArtists($eventId: Int!, $eventArtistIds: [Int!]!) {
    reorderEventArtists(input: { eventId: $eventId, eventArtistIds: $eventArtistIds }) {
      success
      eventArtists {
        id
        offerSheetId
        legacyEventArtistId
        legacySettlementSheetId
        artist {
          ...ArtistFields
        }
      }
    }
  }
  ${ARTIST_FIELDS}
`;

type ReorderEventArtistsParams = {
  eventId: number;
  eventArtistIds: number[];
};

export const reorderEventArtists = async (client: ApolloClient<Query>, reorderParams: ReorderEventArtistsParams): Promise<VPResult<EventArtist.EventArtist[]>> => {
  const { data } = await client.mutate({
    mutation: REORDER_EVENT_ARTISTS_MUTATION,
    variables: {
      ...reorderParams,
    },
    update: cache => {
      cache.evict({
        id: 'ROOT_QUERY',
        // Probably needs better cache clearing
        fieldName: 'app',
      });
      cache.gc();
    },
  });

  const response = data.reorderEventArtists;
  if (response.success) {
    return {
      success: true,
      data: EventArtist.fromGqlCollection(response.eventArtists),
    };
  }

  return {
    success: false,
    errors: [],
  };
};
